import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation, Outlet } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout, { NoSidebarLayout } from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import { useSelector } from 'react-redux';
import authService from 'services/auth.service';
import { PATH_AUTH } from './paths';
// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/app');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    // Dashboard Routes
    { path: 'app/s/onboarding', element: <NoSidebarLayout />, children: [{element: <SOnboarding/>}]},
    {
      path: 'app',
      element: authService.isSignedIn() ?  <DashboardLayout/> : <Navigate to={PATH_AUTH.login} />,
      // element: <DashboardLayout />,
      children: [
        
        {
          path: 's',
          element: authService.isStudent() ? <Outlet />: <Navigate to={PATH_AUTH.login} />,
          children: [
            { element: <Navigate to="/app/s/tabs" replace /> },
            { path: 'dashboard', element: <SDashboard /> },
            { path: 'documents', element: <SDocuments /> },
            { path: 'tabs', element: <STabOverview /> },
            { path: 'tabs/:tabId', element: <STab /> },
            { path: 'settings', element: <SSettings /> },
            { path: 'finances', element: <SFinancials /> },
            { path: 'projects', element: <SProjects /> },
            { path: 'projects/:id', element: <SProjectsView /> },
            { path: 'data-sharing', element: <SDataSharing /> },
            { path: 'chat', element: <Chat /> },
          ]
        },
        {
          path: 'a',
          element: authService.isAdmin() ? <Outlet />: <Navigate to={PATH_AUTH.login} />,
          children: [
            { element: <Navigate to="/app/e/dashboard" replace /> },
            { path: 'tabeditor', element: <ATabEditorOverview /> },
            { path: 'tabeditor/:tabId', element: <ATabEditor /> },
            { path: 'newuser', element: <ANewUser /> },
            { path: 'employees', element: <AEmployeeOverview /> },
            { path: 'exports', element: <AExportPortal /> },
            { path: 'locationeditor', element: <ALocationEditor/> },
            { path: 'finances', element: <AFinanceOverview /> },
            { path: 'budgets', element: <ABudgetOverview />}
          ]
        },
        {
          path: 'e',
          element: authService.isEmployeeReferrerOrSpectator() ? <Outlet />: <Navigate to={PATH_AUTH.login} />,
          children: [
            { element: (authService.isEmployee() ? <Navigate to="/app/e/dashboard" replace /> :  <Navigate to="/app/e/students" replace />)},
            { path: 'dashboard', element: <EDashboard /> },
            { path: 'beta', element: <EBeta /> },
            { path: 'students/:studentId', element: <EStudentDetails /> },
            { path: 'students/:studentId/:pane', element: <EStudentDetails /> },
            { path: 'students/:studentId/tabs/:tabId', element: <EStudentTab /> },
            { path: 'students', element: <EStudentOverview2 /> },
            { path: 'tagManagement', element: <ETagManagement /> },
            { path: 'newstudent', element: <ENewStudent /> },
            { path: 'password', element: <EChangePassword/>},
            { path: 'mfa', element: <MFAStep1 /> },
            { path: 'projects', element: <EProjects /> },
            { path: 'projects/:id', element: <EProjectsView /> },
            { path: 'projects/:project_id/events/new', element: <EProjectsEventsNew /> },
            { path: 'projects/:project_id/events/:event_id', element: <EProjectsEventsDetail /> },
            { path: 'projects/:id/edit', element: <EProjectsEdit /> },
            { path: 'projects/new', element: <EProjectsNew /> },
            { path: 'chat', element: <Chat /> },
            { path: 'docs', element: <EFileManager /> },
            { path: 'docs/open/:file', element: <EFileViewer /> },
          ]
        },
        { path: 'chat', element: <Navigate to={authService.isStudent() ? "/app/s/chat" : "/app/e/chat"}></Navigate>},
        { path: '*', element: <Navigate to={authService.isStudent() ? "/app/s/dashboard" : ( authService.isEmployeeReferrerOrSpectator() ? "/app/e/students" :"/app/e/dashboard")} replace /> }
      
      ]
    },

    // Main Routes
    {
      path: 'auth',
      children: [
        { path: 'login', element: <Login /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'reset-password-t/:token', element: <ResetPassword2 /> },
        { path: 'parent/:token', element: <ParentAuthorization/>},
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        // { path: 'login', element: <Login /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        {element:  authService.isEmployee() ? <Navigate to="/app/e/dashboard" replace /> : 
        (authService.isEmployeeReferrerOrSpectator() ? <Navigate to="/app/e/students" replace /> : 
        (authService.isStudent() ?  <Navigate to="/app/s/tabs" replace /> : 
        <Navigate to="/auth/login" replace />))}
        // { element: <LandingPage /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// IMPORT COMPONENTS

// Dashboard
const AEmployeeOverview = Loadable(lazy(() => import('../pages/admin/EmployeeOverview')));
const AExportPortal = Loadable(lazy(() => import('../pages/admin/ExportPortal')));
const AFinanceOverview = Loadable(lazy(() => import('../pages/admin/FinanceOverview')));
const ANewUser = Loadable(lazy(() => import('../pages/admin/NewUser')));
const ATabEditorOverview = Loadable(lazy(() => import('../pages/admin/TabsOverview')));
const ATabEditor = Loadable(lazy(() => import('../pages/admin/TabEditor')));
const ABudgetOverview = Loadable(lazy(() => import('../pages/admin/budgets/BudgetOverview')));
const EDashboard = Loadable(lazy(() => import('../pages/employee/Dashboard')));
const ALocationEditor = Loadable(lazy(() => import('../pages/employee/LocationEditor')));
const ETagManagement = Loadable(lazy(() => import('../pages/employee/TagOverview')));
const EChangePassword = Loadable(lazy(() => import('../pages/employee/ChangePassword')));
const EBeta = Loadable(lazy(() => import('../pages/employee/Beta')));
const ENewStudent = Loadable(lazy(() => import('../pages/employee/NewStudent')));
const EStudentOverview2 = Loadable(lazy(() => import('../pages/employee/StudentOverviewNew')));
const EStudentDetails = Loadable(lazy(() => import('../pages/employee/student/StudentDetails')));
const EStudentTab = Loadable(lazy(() => import('../pages/employee/student/StudentTab')));
const EProjects = Loadable(lazy(() => import('../pages/employee/Projects')));
const EProjectsView = Loadable(lazy(() => import('../pages/employee/ProjectsDetails')));
const EFileManager = Loadable(lazy(() => import('../pages/employee/Docs')));
const EFileViewer = Loadable(lazy(() => import('../pages/employee/DocViewer')));
const SDocuments = Loadable(lazy(() => import('../pages/student/Documents')));
const SProjects = Loadable(lazy(() => import('../pages/student/Projects')));
const SProjectsView = Loadable(lazy(() => import('../pages/student/ProjectsDetails')));
const SDataSharing = Loadable(lazy(() => import('../pages/student/DataSharing')));
const EProjectsNew = Loadable(lazy(() => import('../pages/employee/ProjectsNew')));
const EProjectsEdit = Loadable(lazy(() => import('../pages/employee/ProjectsEdit')));
const EProjectsEventsNew = Loadable(lazy(() => import('../pages/employee/ProjectsEventsNew')));
const EProjectsEventsDetail =  Loadable(lazy(() => import('../pages/employee/ProjectsEventsDetails')));
const SDashboard = Loadable(lazy(() => import('../pages/student/Dashboard')));
const SOnboarding = Loadable(lazy(() => import('../pages/student/Onboarding')));
const SFinancials = Loadable(lazy(() => import('../pages/student/Financials')));
const STabOverview = Loadable(lazy(() => import('../pages/student/TabOverview')));
const STab = Loadable(lazy(() => import('../pages/student/Tab')));
const Chat = Loadable(lazy(() => import('../pages/shared/Chat')));
const SSettings = Loadable(lazy(() => import('../pages/student/Profile')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const ResetPassword2 = Loadable(lazy(() => import('../pages/authentication/ResetPassword2')));
const ParentAuthorization = Loadable(lazy(() => import('../pages/authentication/ParentAuthorization')));
const MFAStep1 = Loadable(lazy(() => import('../pages/authentication/MFAStep1')));
// const MFAStep2 = Loadable(lazy(() => import('../pages/authentication/MFAStep2')));

const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
